import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import { usePageMeta } from '../../hooks/usePageMeta';

export default function TermsPage() {
  // 設定服務條款頁 Meta
  usePageMeta({
    title: '服務條款 | laigame手遊代儲',
    description: 'laigame手遊代儲服務條款，詳細說明會員註冊、訂單成立、付款規則、代儲服務、退款政策等重要規定，使用本網站服務前請詳細閱讀。',
    keywords: '服務條款,使用條款,訂單規則,退款政策,laigame',
    ogTitle: '服務條款 | laigame手遊代儲',
    ogDescription: 'laigame手遊代儲服務條款，詳細說明會員註冊、訂單成立、付款規則、代儲服務、退款政策等重要規定'
  });

  useEffect(() => {
    const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

    const webpageSchema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": `${SITE_URL}/terms#webpage`,
      "name": "服務條款 - laigame手遊代儲",
      "description": "laigame手遊代儲服務條款，使用本網站服務前請詳細閱讀",
      "url": `${SITE_URL}/terms`,
      "inLanguage": "zh-TW",
      "isPartOf": {
        "@type": "WebSite",
        "@id": `${SITE_URL}/#website`,
        "name": "laigame手遊代儲",
        "url": SITE_URL
      }
    };

    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "@id": `${SITE_URL}/terms#breadcrumb`,
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "首頁",
          "item": `${SITE_URL}/`
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "服務條款",
          "item": `${SITE_URL}/terms`
        }
      ]
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'terms-schema';
    script.text = JSON.stringify(webpageSchema);
    document.head.appendChild(script);

    const breadcrumbScript = document.createElement('script');
    breadcrumbScript.type = 'application/ld+json';
    breadcrumbScript.id = 'terms-breadcrumb-schema';
    breadcrumbScript.text = JSON.stringify(breadcrumbSchema);
    document.head.appendChild(breadcrumbScript);

    return () => {
      ['terms-schema', 'terms-breadcrumb-schema'].forEach(sid => {
        const el = document.getElementById(sid);
        if (el) el.remove();
      });
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <Header />
      
      {/* Hero Banner */}
      <div className="relative bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white py-16 overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center space-x-2 text-sm mb-6">
            <Link to="/" className="hover:text-blue-200 transition-colors">首頁</Link>
            <i className="ri-arrow-right-s-line text-blue-200"></i>
            <span className="text-blue-100">服務條款</span>
          </nav>
          
          <h1 className="text-4xl md:text-5xl font-bold mb-4">服務條款</h1>
          <p className="text-lg text-blue-100 max-w-2xl">
            使用本網站服務前，請詳細閱讀以下條款。使用本網站即表示您同意遵守本服務條款。
          </p>
          <div className="mt-6 text-sm text-blue-100">
            最後更新日期：2024 年 1 月
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
          
          {/* Section 1 */}
          <section className="mb-12">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center mr-4">
                <i className="ri-file-text-line text-white text-xl"></i>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">一、服務說明與適用範圍</h2>
            </div>
            <div className="pl-16 space-y-4 text-gray-700 leading-relaxed">
              <p>1.1 本網站（以下簡稱「本站」）提供手機遊戲代儲值、點數卡販售等相關服務。</p>
              <p>1.2 本服務條款適用於所有使用本站服務的會員及訪客。</p>
              <p>1.3 使用本站服務即表示您已閱讀、瞭解並同意接受本服務條款之所有內容。</p>
              <p>1.4 本站保留隨時修改或變更本服務條款之權利，修改後的條款將公布於本網站，不另行個別通知。</p>
              <p>1.5 若您不同意本服務條款的修改，請立即停止使用本站服務。</p>
            </div>
          </section>

          {/* Section 2 */}
          <section className="mb-12">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center mr-4">
                <i className="ri-user-add-line text-white text-xl"></i>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">二、會員註冊與帳號安全</h2>
            </div>
            <div className="pl-16 space-y-4 text-gray-700 leading-relaxed">
              <p>2.1 註冊會員時，您必須提供真實、正確、最新及完整的個人資料。</p>
              <p>2.2 您應妥善保管您的帳號及密碼，不得將帳號、密碼提供予第三人使用。</p>
              <p>2.3 若發現帳號遭他人冒用或有任何安全問題，請立即通知本站客服。</p>
              <p>2.4 每個會員帳號僅限本人使用，不得轉讓、出借或分享予他人。</p>
              <p>2.5 本站有權在不事先通知的情況下，暫停或終止違反本條款之會員帳號。</p>
              <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r-lg mt-4">
                <div className="flex items-start">
                  <i className="ri-alert-line text-amber-600 text-xl mr-3 mt-0.5"></i>
                  <div>
                    <p className="font-semibold text-amber-900 mb-1">重要提醒</p>
                    <p className="text-amber-800 text-sm">為保障您的權益，請勿與他人共用帳號，因帳號共用所產生的任何糾紛，本站概不負責。</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 3 */}
          <section className="mb-12">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center mr-4">
                <i className="ri-shopping-cart-line text-white text-xl"></i>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">三、訂單成立與付款規則</h2>
            </div>
            <div className="pl-16 space-y-4 text-gray-700 leading-relaxed">
              <p>3.1 當您完成訂單送出並選擇付款方式後，訂單即成立。</p>
              <p>3.2 未完成付款的訂單將於下單後隔天 23:59 自動取消，繳費代碼同時失效。</p>
              <p>3.3 本站提供以下付款方式：</p>
              <ul className="list-disc list-inside pl-4 space-y-2">
                <li>ATM 轉帳（玉山銀行、中國信託、土地銀行）</li>
                <li>超商代碼繳費（7-ELEVEN、全家、萊爾富、OK超商）</li>
                <li>購物金折抵（需為註冊會員）</li>
              </ul>
              <p>3.4 付款完成後，請保留交易憑證以便查詢。</p>
              <p>3.5 如有優惠券或折扣碼，請於結帳時輸入，結帳完成後恕無法補登。</p>
              <p>3.6 訂單金額以新台幣（NT$）計價，不含其他國家或地區之稅費。</p>
            </div>
          </section>

          {/* Section 4 */}
          <section className="mb-12">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl flex items-center justify-center mr-4">
                <i className="ri-gamepad-line text-white text-xl"></i>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">四、代儲服務說明與免責條款</h2>
            </div>
            <div className="pl-16 space-y-4 text-gray-700 leading-relaxed">
              <p>4.1 手遊代儲服務為人工作業，正常處理時間約 30-60 分鐘，實際時間依訂單量而定。</p>
              <p>4.2 付款完成後，請勿登入遊戲帳號，以免造成儲值失敗。</p>
              <p>4.3 下單時請確認遊戲帳號、密碼、伺服器等資訊正確無誤，因資訊錯誤導致儲值失敗，本站不負賠償責任。</p>
              <p>4.4 本站僅提供代儲服務，不保證遊戲內商品、活動或獎勵之有效性，相關問題請洽遊戲官方。</p>
              <p>4.5 若您的遊戲帳號內無該商品品項，本站亦無法進行購買。</p>
              <p>4.6 首儲雙倍或其他遊戲獎勵依照遊戲官方規定，本站無法額外提供。</p>
              <p>4.7 本站最重視帳密安全，網站不記錄任何客戶的遊戲帳號密碼。</p>
              <p>4.8 點卡商品於付款完成後，系統將自動發送儲值代碼簡訊至您的手機。</p>
              <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg mt-4">
                <div className="flex items-start">
                  <i className="ri-error-warning-line text-red-600 text-xl mr-3 mt-0.5"></i>
                  <div>
                    <p className="font-semibold text-red-900 mb-1">免責聲明</p>
                    <p className="text-red-800 text-sm">因遊戲官方政策變更、帳號異常、鎖區限制等非本站可控因素導致儲值失敗，本站將協助處理但不負賠償責任。急用、活動或限時商品請斟酌下單。</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 5 */}
          <section className="mb-12">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl flex items-center justify-center mr-4">
                <i className="ri-refund-2-line text-white text-xl"></i>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">五、退款政策</h2>
            </div>
            <div className="pl-16 space-y-4 text-gray-700 leading-relaxed">
              <p>5.1 以下情況可申請退款：</p>
              <ul className="list-disc list-inside pl-4 space-y-2">
                <li>訂單尚未開始處理且未完成儲值</li>
                <li>因本站作業疏失導致儲值錯誤</li>
                <li>重複付款或誤付款項</li>
              </ul>
              <p>5.2 以下情況恕不接受退款：</p>
              <ul className="list-disc list-inside pl-4 space-y-2">
                <li>已完成儲值的訂單</li>
                <li>點卡序號已發送的訂單</li>
                <li>因客戶提供錯誤資訊導致儲值失敗</li>
                <li>因遊戲官方政策或帳號問題導致無法儲值</li>
              </ul>
              <p>5.3 退款處理時間約 10-15 個工作天（不含假日），因採批次處理，實際時間可能較長，敬請見諒。</p>
              <p>5.4 退款將退回原付款帳戶或方式，無法指定其他帳戶。</p>
              <p>5.5 使用購物金折抵的部分，退款時將退回購物金，不退現金。</p>
            </div>
          </section>

          {/* Section 6 */}
          <section className="mb-12">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-pink-600 rounded-xl flex items-center justify-center mr-4">
                <i className="ri-forbid-line text-white text-xl"></i>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">六、禁止行為</h2>
            </div>
            <div className="pl-16 space-y-4 text-gray-700 leading-relaxed">
              <p>6.1 使用本站服務時，您不得從事以下行為：</p>
              <ul className="list-disc list-inside pl-4 space-y-2">
                <li>提供虛假、不實或誤導性的資訊</li>
                <li>冒用他人名義或盜用他人帳號</li>
                <li>利用本站服務從事詐欺、洗錢或其他違法行為</li>
                <li>惡意大量下單或取消訂單，影響本站正常營運</li>
                <li>使用自動化程式或機器人進行下單</li>
                <li>散布病毒、惡意程式或進行任何破壞網站系統之行為</li>
                <li>未經授權存取本站系統或資料庫</li>
              </ul>
              <p>6.2 <strong>代儲同業禁止使用推薦代碼</strong>：如發現使用推薦代碼者為代儲同業，本站可無條件取消該帳戶購物金權利及會員資格。</p>
              <p>6.3 若您違反上述規定，本站有權立即暫停或終止您的帳號，並保留法律追訴權。</p>
            </div>
          </section>

          {/* Section 7 */}
          <section className="mb-12">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center mr-4">
                <i className="ri-gift-line text-white text-xl"></i>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">七、代理回饋制度</h2>
            </div>
            <div className="pl-16 space-y-4 text-gray-700 leading-relaxed">
              <p>7.1 代理回饋制度詳細內容請參閱 <Link to="/agent" className="text-blue-600 hover:text-blue-700 font-semibold underline">代理回饋制度說明頁面</Link>。</p>
              <p>7.2 本活動僅適用於手遊遊戲，不適用於海外點數代付服務及台灣點數卡。</p>
              <p>7.3 購物金無使用期限，可於結帳時折抵訂單金額。</p>
              <p>7.4 現金回饋需綁定銀行帳戶，帳戶綁定後無法隨意修改，銀行帳戶與會員帳號僅能一對一使用。</p>
              <p>7.5 本站保有隨時修改、暫停或終止代理回饋制度之權利。</p>
            </div>
          </section>

          {/* Section 8 */}
          <section className="mb-12">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-gray-500 to-slate-600 rounded-xl flex items-center justify-center mr-4">
                <i className="ri-shield-check-line text-white text-xl"></i>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">八、智慧財產權</h2>
            </div>
            <div className="pl-16 space-y-4 text-gray-700 leading-relaxed">
              <p>8.1 本站所有內容，包括但不限於文字、圖片、圖形、標誌、按鈕圖示、軟體等，均為本站或其授權人所有。</p>
              <p>8.2 未經本站書面同意，您不得重製、修改、散布、展示或以其他方式使用本站內容。</p>
              <p>8.3 遊戲相關圖片、商標、名稱等智慧財產權歸各遊戲官方所有，本站僅提供代儲服務。</p>
            </div>
          </section>

          {/* Section 9 */}
          <section className="mb-12">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl flex items-center justify-center mr-4">
                <i className="ri-edit-line text-white text-xl"></i>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">九、條款修改權</h2>
            </div>
            <div className="pl-16 space-y-4 text-gray-700 leading-relaxed">
              <p>9.1 本站保留隨時修改本服務條款之權利，修改後的條款將公布於本網站。</p>
              <p>9.2 若您不同意修改後的條款，請立即停止使用本站服務。</p>
              <p>9.3 繼續使用本站服務即表示您同意接受修改後的服務條款。</p>
              <p>9.4 重大條款變更時，本站將於網站首頁或透過電子郵件通知會員。</p>
            </div>
          </section>

          {/* Section 10 */}
          <section className="mb-12">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-rose-600 rounded-xl flex items-center justify-center mr-4">
                <i className="ri-scales-line text-white text-xl"></i>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">十、準據法與管轄法院</h2>
            </div>
            <div className="pl-16 space-y-4 text-gray-700 leading-relaxed">
              <p>10.1 本服務條款之解釋與適用，以及與本服務條款有關的爭議，均應依照中華民國法律予以處理。</p>
              <p>10.2 因本服務條款所生之爭議，雙方同意以台灣台北地方法院為第一審管轄法院。</p>
            </div>
          </section>

          {/* Contact */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-8 mt-12">
            <div className="flex items-start">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                <i className="ri-customer-service-2-line text-white text-xl"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">如有任何疑問</h3>
                <p className="text-gray-700 mb-4">若您對本服務條款有任何疑問，歡迎透過以下方式聯繫我們：</p>
                <div className="flex flex-wrap gap-4">
                  <a href="https://line.me/ti/p/@laigame" target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl hover:from-green-600 hover:to-emerald-700 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer">
                    <i className="ri-line-fill text-xl mr-2"></i>
                    LINE 客服
                  </a>
                  <a href="https://www.facebook.com/laigame" target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer">
                    <i className="ri-facebook-fill text-xl mr-2"></i>
                    Facebook 粉專
                  </a>
                </div>
                <p className="text-sm text-gray-600 mt-4">客服時間：24 小時全年無休</p>
              </div>
            </div>
          </div>

        </div>
      </div>

      <Footer />
    </div>
  );
}